/**
 * attachmentMedia.ts — the media facts an attachment carries to the chat (2026-10-13, docs/PLAN-screen-capture-20261013.md,
 * PR C): what kind of media a file is, so the chat can draw it well. A computer run's video carries its length, that it
 * is silent, and its chapters (one per step, named by the planner's short caption); a run's screenshot says it is one.
 * Closed and bounded: closed words, whole numbers and those captions, never a path, an address or page text.
 *
 * TWIN: functions/src/prest0nSession/attachmentMedia.ts and src/components/GlobalChatSystem/attachmentMedia.ts are
 * byte-identical (pinned by functions/src/__tests__/aCaptureKeepsItsMediaFacts.test.ts): what the server writes is what
 * the client reads, and neither side can start writing a value the other cannot read.
 */

/** What a file is to the chat's player. */
export const ATTACHMENT_MEDIA_KINDS = ['video', 'image', 'audio'] as const;
export type AttachmentMediaKind = (typeof ATTACHMENT_MEDIA_KINDS)[number];

/** A picture of work a computer run did: its own video, or one of its screenshots (never a file it read). */
export const ATTACHMENT_CAPTURES = ['recording', 'screenshot'] as const;
export type AttachmentCapture = (typeof ATTACHMENT_CAPTURES)[number];

/** A video's chapters kept, and each label's length (the workstation keeps no more). */
export const MEDIA_CHAPTERS_MAX = 60;
export const MEDIA_CHAPTER_LABEL_MAX = 80;
/** The longest length read, in whole seconds, and the largest side, in pixels. */
export const MEDIA_SECONDS_MAX = 24 * 3600;
export const MEDIA_SIDE_MAX = 16384;

export interface AttachmentMediaChapter {
  /** Where the step begins, in whole seconds from the start. */
  at: number;
  label: string;
}

export interface AttachmentMedia {
  kind: AttachmentMediaKind;
  /** Its length in whole seconds, when known. */
  seconds?: number;
  /** It has no sound (a run's video never does). */
  silent?: boolean;
  /** Where each step begins, in order. */
  chapters?: AttachmentMediaChapter[];
  capture?: AttachmentCapture;
}

/** The media fields an attachment record carries: its size in pixels beside its media facts. */
export interface AttachmentMediaFields {
  width?: number;
  height?: number;
  media?: AttachmentMedia;
}

function wholeIn(raw: unknown, min: number, max: number): number | undefined {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= min && raw <= max ? raw : undefined;
}

/** A label as one plain line: invisible and control characters become spaces, then spaces collapse. */
function labelOf(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  let line = '';
  for (const ch of raw) {
    const c = ch.codePointAt(0) || 0;
    const invisible = c < 0x20 || (c >= 0x7f && c <= 0x9f) || (c >= 0x200b && c <= 0x200f)
      || (c >= 0x202a && c <= 0x202e) || (c >= 0x2066 && c <= 0x2069) || c === 0xfeff;
    line += invisible ? ' ' : ch;
  }
  line = line.replace(/\s+/g, ' ').trim();
  return line.length <= MEDIA_CHAPTER_LABEL_MAX ? line : `${line.slice(0, MEDIA_CHAPTER_LABEL_MAX - 1).trimEnd()}…`;
}

/** PURE. A video's chapters read closed: in order, inside its length, each labelled, at most MEDIA_CHAPTERS_MAX. */
export function mediaChaptersOf(raw: unknown, seconds?: number): AttachmentMediaChapter[] {
  if (!Array.isArray(raw)) return [];
  const out: AttachmentMediaChapter[] = [];
  for (const row of raw) {
    if (out.length >= MEDIA_CHAPTERS_MAX) break;
    if (!row || typeof row !== 'object') continue;
    const r = row as { at?: unknown; label?: unknown };
    const at = wholeIn(r.at, 0, seconds === undefined ? MEDIA_SECONDS_MAX : seconds);
    const label = labelOf(r.label);
    if (at === undefined || !label) continue;
    if (out.length > 0 && at <= out[out.length - 1].at) continue;
    out.push({ at, label });
  }
  return out;
}

/** PURE. The media facts read closed: a malformed field is dropped and the rest kept; undefined without a known kind. */
export function attachmentMediaOf(raw: unknown): AttachmentMedia | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const kind = (ATTACHMENT_MEDIA_KINDS as readonly unknown[]).includes(r.kind) ? (r.kind as AttachmentMediaKind) : undefined;
  if (!kind) return undefined;
  const seconds = kind === 'image' ? undefined : wholeIn(r.seconds, 0, MEDIA_SECONDS_MAX);
  const chapters = kind === 'video' ? mediaChaptersOf(r.chapters, seconds) : [];
  const capture = (ATTACHMENT_CAPTURES as readonly unknown[]).includes(r.capture) ? (r.capture as AttachmentCapture) : undefined;
  return {
    kind,
    ...(seconds !== undefined ? { seconds } : {}),
    ...(kind !== 'image' && r.silent === true ? { silent: true } : {}),
    ...(chapters.length > 0 ? { chapters } : {}),
    ...(capture ? { capture } : {}),
  };
}

/** PURE. An attachment's media fields read closed, ready to spread onto the next record: only what is there and sound. */
export function mediaFieldsOf(att: unknown): AttachmentMediaFields {
  if (!att || typeof att !== 'object') return {};
  const a = att as { width?: unknown; height?: unknown; media?: unknown };
  const width = wholeIn(a.width, 1, MEDIA_SIDE_MAX);
  const height = wholeIn(a.height, 1, MEDIA_SIDE_MAX);
  const media = attachmentMediaOf(a.media);
  return {
    ...(width !== undefined && height !== undefined ? { width, height } : {}),
    ...(media ? { media } : {}),
  };
}
